import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/proApi";

const GOALS = [
  ["30 jours", "Pages par ville et territoire en ligne et explorées ; Search Console et Bing connectés ; 300 prospects contactés depuis le CRM ; 10 inscriptions dans des annuaires fiables."],
  ["90 jours", "Premières impressions Google sur les requêtes « fournisseur beauté afro » ; 600 prospects contactés ; 15 nouveaux comptes pro ; 3 mentions dans des médias ou blogs."],
  ["180 jours", "Trafic organique régulier sur les pages métiers et territoires ; premières visites venant des assistants IA ; 40 comptes pro ; 10 liens entrants de qualité."],
  ["365 jours", "Ozentya cité sur les recherches professionnelles afro en France, DOM-TOM et Belgique ; 100 comptes pro actifs ; partenariats avec salons de formation et médias afro."],
];

const Kpi = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-white border border-[var(--oz-border)] p-4"><p className="text-xs text-[var(--oz-muted)]">{label}</p><p data-testid={testid} className="mt-1 text-2xl font-semibold">{value}</p></div>
);

const Table = ({ title, rows, testid }) => (
  <div className="rounded-2xl bg-white border border-[var(--oz-border)] p-4" data-testid={testid}>
    <h2 className="font-semibold text-sm">{title}</h2>
    {rows.length ? <ul className="mt-2 text-sm divide-y divide-[var(--oz-border)]">{rows.map((r) => (
      <li key={r.name} className="py-1.5 flex justify-between gap-3"><span className="truncate">{r.name || "—"}</span><span className="text-[var(--oz-muted)] whitespace-nowrap">{r.visits} visites · {r.visitors} visiteurs</span></li>))}</ul>
      : <p className="mt-2 text-sm text-[var(--oz-muted)]">Pas encore de données.</p>}
  </div>
);

const UtmBuilder = () => {
  const [f, setF] = useState({ page: "/espace-pro/inscription", source: "email", medium: "prospection", campaign: "salons-afro-2026" });
  const slug = (v) => encodeURIComponent(v.trim().toLowerCase().replace(/\s+/g, "-"));
  const url = `https://ozentya.org${f.page.trim()}?utm_source=${slug(f.source)}&utm_medium=${slug(f.medium)}&utm_campaign=${slug(f.campaign)}`;
  return (
    <section data-testid="utm-builder" className="rounded-2xl bg-white border border-[var(--oz-border)] p-5">
      <h2 className="font-semibold">Créer un lien traçable (UTM)</h2>
      <div className="mt-3 grid sm:grid-cols-4 gap-2">
        {[["page", "Page"], ["source", "Source (ex. instagram)"], ["medium", "Support (ex. post)"], ["campaign", "Campagne"]].map(([k, l]) => (
          <input key={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={l} aria-label={l} data-testid={`utm-${k}`} className="rounded-lg border border-[var(--oz-border)] px-3 py-2 text-sm" />
        ))}
      </div>
      <div className="mt-3 flex gap-2 items-center"><code data-testid="utm-result" className="flex-1 text-xs break-all bg-[var(--oz-bg-2)] rounded-lg p-2">{url}</code>
        <button data-testid="utm-copy" onClick={() => navigator.clipboard?.writeText(url).then(() => toast.success("Lien copié"))} className="p-2 rounded-full border border-[var(--oz-border)]"><Copy className="w-4 h-4" /></button></div>
    </section>
  );
};

export default function AdminStats() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState("");
  useEffect(() => { adminApi.get(`/admin/stats?days=${days}`).then(({ data }) => setD(data)).catch((e) => setErr(errMsg(e))); }, [days]);
  const max = Math.max(1, ...(d?.series || []).map((s) => s.visits));
  return (
    <div data-testid="admin-stats" className="max-w-6xl mx-auto px-4 sm:px-8 py-10 space-y-6">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[var(--oz-muted)]" data-testid="stats-back"><ArrowLeft className="w-4 h-4" /> Administration</Link>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h1 className="font-serif text-3xl sm:text-4xl">Visibilité et statistiques</h1>
          <p className="mt-1 text-sm text-[var(--oz-muted)]">Mesure intégrée, sans cookie : visites humaines (robots exclus), sources, pages et inscriptions pro.</p></div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="stats-days" className="rounded-lg border border-[var(--oz-border)] px-3 py-2 text-sm">
          {[7, 30, 90, 180, 365].map((n) => <option key={n} value={n}>{n} derniers jours</option>)}
        </select>
      </div>
      {err && <p className="text-sm text-red-600" data-testid="stats-error">{err}</p>}
      {d && <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Visites" value={d.visits} testid="kpi-visits" /><Kpi label="Visiteurs" value={d.visitors} testid="kpi-visitors" />
          <Kpi label="Inscriptions pro" value={`${d.signups} (${d.conversion} %)`} testid="kpi-signups" /><Kpi label="Visites depuis les IA" value={d.ai.reduce((a, r) => a + r.visits, 0)} testid="kpi-ai" />
          <Kpi label="Part mobile" value={`${d.mobile_share} %`} testid="kpi-mobile" /><Kpi label="Prospects identifiés" value={d.prospects} testid="kpi-prospects" />
          <Kpi label="Villes couvertes (CRM)" value={d.prospect_cities} testid="kpi-cities" /><Kpi label="Positions / impressions Google" value="Search Console à connecter" testid="kpi-gsc" />
        </div>
        <div className="rounded-2xl bg-white border border-[var(--oz-border)] p-4" data-testid="stats-series">
          <h2 className="font-semibold text-sm">Visites par jour</h2>
          <div className="mt-3 h-32 flex items-end gap-[2px]">{d.series.map((s) => <div key={s.day} title={`${s.day} : ${s.visits}`} className="flex-1 bg-[var(--oz-gold)] rounded-t" style={{ height: `${(s.visits / max) * 100}%`, minHeight: 2 }} />)}</div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <Table title="Types de source" rows={d.kinds} testid="stats-kinds" /><Table title="Sources" rows={d.sources} testid="stats-sources" />
          <Table title="Assistants IA (ChatGPT, Perplexity, Gemini…)" rows={d.ai} testid="stats-ai" /><Table title="Campagnes (liens UTM)" rows={d.campaigns} testid="stats-campaigns" />
        </div>
        <Table title="Pages les plus vues" rows={d.pages} testid="stats-pages" />
      </>}
      <UtmBuilder />
      <section data-testid="stats-goals" className="rounded-2xl bg-white border border-[var(--oz-border)] p-5">
        <h2 className="font-semibold">Objectifs</h2>
        <ul className="mt-2 space-y-2 text-sm">{GOALS.map(([t, g]) => <li key={t}><b>{t}</b> — {g}</li>)}</ul>
        <p className="mt-2 text-xs text-[var(--oz-muted)]">Objectifs indicatifs, non garantis : les positions Google et les réponses des IA ne se commandent pas.</p>
      </section>
    </div>
  );
}
